import { getDataAPI } from './fetchData';
import { remindersManager } from './remindersData';

/**
 * Activity Feed API Service
 * Aggregates data from different apps to create an activity feed
 */

export const activityFeedApi = {
  /**
   * Get activity feed data from all apps
   * @returns {Promise} - Activity feed data
   */
  async getActivityFeed() {
    try {
      const token = localStorage.getItem('dizeeToken');
      
      // Return empty data if no token (user not logged in)
      if (!token) {
        return {
          success: true,
          activities: [],
          counts: {
            bookings: 0,
            catalog: 0,
            merch: 0,
            social: 0,
            playlist: 0,
            charting: 0,
            playlisting: 0,
            'social-listening': 0,
            'spotify-revenue': 0,
            reminders: 0
          }
        };
      }
      
      // Make parallel requests only to apps that don't have cached data
      const [
        bookingsResponse, 
        catalogResponse, 
        merchResponse, 
        socialResponse, 
        playlistResponse
      ] = await Promise.allSettled([
        getDataAPI('user/getBookings', token),
        getDataAPI('user/getCatalog', token),
        getDataAPI('user/getMerch', token),
        getDataAPI('user/get-socialfeed', token),
        getDataAPI('user/get-playlist', token)
      ]);

      // Process existing apps data
      const bookings = bookingsResponse.status === 'fulfilled' && bookingsResponse.value?.data?.status === 200 
        ? this.processBookingsData(bookingsResponse.value.data.data)
        : [];

      const catalog = catalogResponse.status === 'fulfilled' && catalogResponse.value?.data?.status === 200
        ? this.processCatalogData(catalogResponse.value.data.data)
        : [];

      const merch = merchResponse.status === 'fulfilled' && merchResponse.value?.data?.status === 200
        ? this.processMerchData(merchResponse.value.data.data)
        : [];

      const social = socialResponse.status === 'fulfilled' && socialResponse.value?.data?.status === 200
        ? this.processSocialData(socialResponse.value.data.data.response)
        : [];

      const playlist = playlistResponse.status === 'fulfilled' && playlistResponse.value?.data?.status === 200
        ? this.processPlaylistData(playlistResponse.value.data.data.response)
        : [];

      // NEW: Read from cached app data instead of making API calls
      const charting = this.getChartingDataFromCache();
      const socialListening = this.getSocialListeningDataFromCache();
      const reminders = await this.processRemindersData();
      
      // Placeholder for apps not yet implemented
      const playlisting = []; // Will read from cache when implemented
      const spotifyRevenue = []; // Will read from cache when implemented

      // Combine all activities and sort by timestamp
      const allActivities = [
        ...bookings, 
        ...catalog, 
        ...merch, 
        ...social, 
        ...playlist,
        ...charting,
        ...playlisting,
        ...socialListening,
        ...spotifyRevenue,
        ...reminders
      ];
      const sortedActivities = allActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      return {
        success: true,
        activities: sortedActivities,
        counts: {
          bookings: bookings.length,
          catalog: catalog.length,
          merch: merch.length,
          social: social.length,
          playlist: playlist.length,
          charting: charting.length,
          playlisting: playlisting.length,
          'social-listening': socialListening.length,
          'spotify-revenue': spotifyRevenue.length,
          reminders: reminders.length
        }
      };
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch activity feed',
        activities: [],
        counts: {
          bookings: 0,
          catalog: 0,
          merch: 0,
          social: 0,
          playlist: 0,
          charting: 0,
          playlisting: 0,
          'social-listening': 0,
          'spotify-revenue': 0,
          reminders: 0
        }
      };
    }
  },

  /**
   * Format booking date exactly like in BookingsViewAll component
   */
  formatBookingDate(dateString) {
    if (!dateString) return { month: 'TBD', day: '?' };
    
    try {
      // Handle YYYY-MM-DD format directly to avoid timezone issues
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        const monthName = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
        return { month: monthName, day: parseInt(day) };
      }
      
      // Fallback for other date formats
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const day = date.getDate();
      return { month, day };
    } catch {
      const parts = dateString.split(' ');
      if (parts.length >= 2) {
        return { 
          month: parts[0].substring(0, 3).toUpperCase(), 
          day: parts[1].replace(',', '') 
        };
      }
      return { month: 'TBD', day: '?' };
    }
  },

  /**
   * Process catalog data into activity feed format
   */
  processCatalogData(catalogData) {
    const catalog = Array.isArray(catalogData) ? catalogData : (catalogData?.data || []);
    console.log('🎵 Processing catalog data:', catalog.length, 'items');
    
    return catalog.slice(0, 3).map((song, index) => {
      console.log('🎵 Song timestamp data:', {
        id: song._id,
        song_title: song.song_title || song.name,
        artist: song.artist_name || song.artist,
        createdAt: song.createdAt,
        updatedAt: song.updatedAt
      });
      
      // Use the actual mongoose timestamps
      let timestamp = song.createdAt || song.updatedAt;
      
      // If no timestamp or invalid timestamp, use current time minus a few minutes
      if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
        console.log('🎵 No valid timestamp, using fallback:', timestamp);
      } else {
        console.log('🎵 Using mongoose timestamp:', timestamp);
      }
      
      return {
        id: song._id || Math.random().toString(36).substr(2, 9),
        type: 'catalog',
        title: 'New Song Added',
        description: `${song.song_title || song.name || 'Unknown Track'} by ${song.artist_name || song.artist || 'Unknown Artist'}`,
        timestamp: timestamp,
        color: '#17A2B8',
        appName: 'Catalog',
        image: song.album_artwork || song.artwork || null,
        metadata: {
          artist: song.artist_name || song.artist,
          album: song.album_name,
          genre: song.genre,
          duration: song.duration,
          status: song.status,
          isrc: song.isrc,
          recordLabel: song.record_label
        }
      };
    });
  },

  /**
   * Process bookings data into activity feed format
   */
  processBookingsData(bookingsData) {
    const bookings = Array.isArray(bookingsData) ? bookingsData : (bookingsData?.data || []);
    
    return bookings.slice(0, 3).map((booking, index) => {
      const dateInfo = this.formatBookingDate(booking.perf_date);
      
      // Use the actual mongoose timestamps
      let timestamp = booking.createdAt || booking.updatedAt;
      
      // If no timestamp or invalid timestamp, use current time minus a few minutes
      if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
      }
      
      return {
        id: booking._id || Math.random().toString(36).substr(2, 9),
        type: 'bookings',
        title: 'New Booking',
        description: `${booking.event_title || booking.venue || 'Unknown Venue'} - ${booking.city_state || booking.location || 'Location TBD'}`,
        timestamp: timestamp,
        color: '#FFFF00',
        appName: 'Bookings',
        image: null,
        dateDisplay: dateInfo,
        metadata: {
          venue: booking.venue,
          artist: booking.artist,
          location: booking.city_state || booking.location,
          ticketsSold: booking.tix_sold || booking.current_ticket_count || 0,
          capacity: booking.sellable_cap,
          guarantee: booking.guarantee,
          promoter: booking.promoter,
          status: booking.status
        }
      };
    });
  },

  /**
   * Process merch data into activity feed format
   */
  processMerchData(merchData) {
    const merch = Array.isArray(merchData) ? merchData : (merchData?.data || []);
    console.log('🛍️ Processing merch data:', merch.length, 'items');
    
    return merch.slice(0, 3).map((item, index) => {
      console.log('🛍️ Merch timestamp data:', {
        id: item._id,
        product_name: item.product_name,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      });
      
      // Use the actual mongoose timestamps
      let timestamp = item.createdAt || item.updatedAt;
      
      // If no timestamp or invalid timestamp, use current time minus a few minutes
      if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
        console.log('🛍️ No valid timestamp, using fallback:', timestamp);
      } else {
        console.log('🛍️ Using mongoose timestamp:', timestamp);
      }
      
      return {
        id: item._id || Math.random().toString(36).substr(2, 9),
        type: 'merch',
        title: 'New Merch Item',
        description: `${item.product_name || 'Unknown Product'} - ${item.vendor || item.vendor_brand || 'Unknown Brand'}`,
        timestamp: timestamp,
        color: '#9932CC',
        appName: 'Merch',
        image: item.image || item.product_image || null,
        metadata: {
          productType: item.product_type,
          quantity: item.inventory_quantity || item.quantity,
          price: item.price,
          sku: item.sku,
          status: item.status || item.product_status,
          brand: item.vendor || item.vendor_brand
        }
      };
    });
  },

  /**
   * Process reminders data into activity feed format (active reminders only)
   */
  async processRemindersData() {
    try {
      console.log('⏰ Processing active reminders data');
      
      // Get reminders summary from API (fast call)
      const { remindersApi } = await import('./remindersApi');
      const result = await remindersApi.getRemindersSummary();
      
      if (!result.success) {
        console.log('⏰ Failed to get reminders summary:', result.error);
        return [];
      }

      const { overdue, upcoming } = result.summary;
      const activities = [];

      // Add overdue reminders activity
      if (overdue > 0) {
        activities.push({
          id: 'overdue_reminders',
          type: 'reminder',
          title: `${overdue} Overdue Reminder${overdue > 1 ? 's' : ''}`,
          description: `You have ${overdue} overdue reminder${overdue > 1 ? 's' : ''} that need attention`,
          timestamp: new Date().toISOString(),
          priority: 'high',
          actionUrl: '/reminders?tab=overdue',
          metadata: {
            count: overdue,
            status: 'overdue'
          }
        });
      }

      // Add upcoming reminders activity
      if (upcoming > 0) {
        activities.push({
          id: 'upcoming_reminders',
          type: 'reminder',
          title: `${upcoming} Upcoming Reminder${upcoming > 1 ? 's' : ''}`,
          description: `You have ${upcoming} upcoming reminder${upcoming > 1 ? 's' : ''} to keep track of`,
          timestamp: new Date().toISOString(),
          priority: 'medium',
          actionUrl: '/reminders?tab=upcoming',
          metadata: {
            count: upcoming,
            status: 'upcoming'
          }
        });
      }

      console.log('⏰ Processed reminders summary:', { overdue, upcoming, activitiesCreated: activities.length });
      return activities;
    } catch (error) {
      console.error('Error processing reminders data:', error);
      return [];
    }
  },

  /**
   * Process social data into activity feed format
   */
  processSocialData(socialData) {
    const social = Array.isArray(socialData) ? socialData : [];
    console.log('📱 Processing social data:', social.length, 'items');
    
    return social.slice(0, 2).map((post, index) => {
      console.log('📱 Social timestamp data:', {
        id: post._id,
        createdAt: post.createdAt,
        updatedAt: post.updatedAt,
        timestamp: post.timestamp
      });
      
      // Use the activity timestamp or fallback
      let timestamp = post.timestamp || post.createdAt || post.updatedAt;
      
      // If no timestamp or invalid timestamp, use current time minus a few minutes
      if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
        console.log('📱 No valid timestamp, using fallback:', timestamp);
      } else {
        console.log('📱 Using mongoose timestamp:', timestamp);
      }
      
      return {
        id: post._id || Math.random().toString(36).substr(2, 9),
        type: 'social',
        title: 'Social Post',
        description: `New ${post.platform || 'social'} post: ${post.title || 'Untitled'}`,
        timestamp: timestamp,
        color: '#FF6B6B',
        appName: 'Social',
        image: post.avatar || null,
        metadata: {
          platform: post.platform,
          title: post.title,
          publishStatus: post.publish?.value || 'draft',
          links: post.links
        }
      };
    });
  },

  /**
   * Process playlist data into activity feed format
   */
  processPlaylistData(playlistData) {
    const playlist = Array.isArray(playlistData) ? playlistData : [];
    console.log('🎶 Processing playlist data:', playlist.length, 'items');
    
    return playlist.slice(0, 2).map((item, index) => {
      console.log('🎶 Playlist timestamp data:', {
        id: item._id,
        title: item.title,
        artist: item.artist,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      });
      
      // Use the actual mongoose timestamps
      let timestamp = item.createdAt || item.updatedAt;
      
      // If no timestamp or invalid timestamp, use current time minus a few minutes
      if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
        console.log('🎶 No valid timestamp, using fallback:', timestamp);
      } else {
        console.log('🎶 Using mongoose timestamp:', timestamp);
      }
      
      return {
        id: item._id || Math.random().toString(36).substr(2, 9),
        type: 'playlist',
        title: 'Playlist Added',
        description: `${item.title || 'Unknown Playlist'} by ${item.artist || 'Unknown Artist'}`,
        timestamp: timestamp,
        color: '#34C759',
        appName: 'Playlist',
        image: item.avatar || null,
        metadata: {
          artist: item.artist,
          platform: item.links?.[0]?.source || 'unknown',
          publishStatus: item.publish?.value || 'draft',
          links: item.links
        }
      };
    });
  },

  // Note: Old API-based processing functions removed - now reading from cached app data

  /**
   * Format time for display (e.g., "2h", "5m", "1d")
   */
  formatTimeAgo(timestamp) {
    console.log('📅 formatTimeAgo called with:', timestamp, 'Type:', typeof timestamp);
    
    if (!timestamp) {
      console.log('📅 No timestamp provided, returning "now"');
      return 'now';
    }
    
    const now = new Date();
    console.log('📅 Current time:', now.toISOString());
    
    // Handle different timestamp formats
    let time;
    if (typeof timestamp === 'string') {
      // Try to parse the string
      time = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      time = timestamp;
    } else if (typeof timestamp === 'number') {
      // Handle Unix timestamp (seconds or milliseconds)
      time = new Date(timestamp < 1000000000000 ? timestamp * 1000 : timestamp);
    } else {
      console.log('📅 Invalid timestamp format, returning "now"');
      return 'now';
    }
    
    console.log('📅 Parsed time:', time.toISOString());
    
    // Check if the date is valid
    if (isNaN(time.getTime())) {
      console.log('📅 Invalid date, returning "now"');
      return 'now';
    }
    
    const diffInMs = now - time;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    
    console.log('📅 Time difference:', diffInMs, 'ms =', diffInMinutes, 'minutes');
    
    if (diffInMinutes < 0) {
      console.log('📅 Future date detected, returning "now"');
      return 'now';
    } else if (diffInMinutes < 1) {
      console.log('📅 Less than 1 minute ago, returning "now"');
      return 'now';
    } else if (diffInMinutes < 60) {
      const result = `${diffInMinutes}m`;
      console.log('📅 Minutes ago:', result);
      return result;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      const result = `${hours}h`;
      console.log('📅 Hours ago:', result);
      return result;
    } else if (diffInMinutes < 10080) { // 7 days
      const days = Math.floor(diffInMinutes / 1440);
      const result = `${days}d`;
      console.log('📅 Days ago:', result);
      return result;
    } else {
      const weeks = Math.floor(diffInMinutes / 10080);
      const result = `${weeks}w`;
      console.log('📅 Weeks ago:', result);
      return result;
    }
  },

  /**
   * Get charting data from cached app data instead of API call
   */
  getChartingDataFromCache() {
    try {
      const cachedData = localStorage.getItem('charting_app_data');
      if (!cachedData) {
        console.log('📊 No cached charting data found');
        return [];
      }

      const parsed = JSON.parse(cachedData);
      const chartData = parsed.chartData || [];
      
      console.log('📊 Processing cached charting data:', chartData.length, 'items');
      
      // Convert cached chart data to activity feed format
      return chartData.slice(0, 3).map((chart, index) => {
        // Use chart date or fallback to recent time
        let timestamp = chart.date;
        if (!timestamp || isNaN(new Date(timestamp).getTime())) {
          timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
        }

        return {
          id: chart.id || Math.random().toString(36).substr(2, 9),
          type: 'charting',
          title: 'Chart Position',
          description: `${chart.track_name || 'Track'} reached #${chart.position} on ${chart.chart_name || 'Chart'}`,
          timestamp: timestamp,
          color: '#FF9500',
          appName: 'Charting',
          image: null,
          metadata: {
            artist: chart.artist_name,
            track: chart.track_name,
            position: chart.position,
            platform: chart.platform,
            chart: chart.chart_name,
            country: chart.country
          }
        };
      });
    } catch (error) {
      console.error('❌ Error reading cached charting data:', error);
      return [];
    }
  },

  /**
   * Get social listening data from cached app data instead of API call
   */
  getSocialListeningDataFromCache() {
    try {
      const cachedData = localStorage.getItem('social_listening_app_data');
      if (!cachedData) {
        console.log('💬 No cached social listening data found');
        return [];
      }

      const parsed = JSON.parse(cachedData);
      const socialData = parsed.socialData || [];
      
      console.log('💬 Processing cached social listening data:', socialData.length, 'items');
      
      // Convert cached social data to activity feed format
      return socialData.slice(0, 2).map((mention, index) => {
        // Use mention date or fallback to recent time
        let timestamp = mention.created_at;
        if (!timestamp || isNaN(new Date(timestamp).getTime())) {
          timestamp = new Date(Date.now() - (index * 5 * 60 * 1000)).toISOString();
        }

        return {
          id: mention.id || Math.random().toString(36).substr(2, 9),
          type: 'social-listening',
          title: 'Social Mention',
          description: `${mention.curator_name || 'User'} mentioned your music on ${mention.platform || 'social media'}`,
          timestamp: timestamp,
          color: '#007AFF',
          appName: 'Social Listening',
          image: null,
          metadata: {
            platform: mention.platform,
            curator: mention.curator_name,
            content: mention.content,
            likes: mention.likes,
            shares: mention.shares,
            artist: mention.artist_name,
            track: mention.track_name
          }
        };
      });
    } catch (error) {
      console.error('❌ Error reading cached social listening data:', error);
      return [];
    }
  }
};

export default activityFeedApi; 